/**
 * ComparisonNotesDialog — modale notes contextuelles (Chunk 9.Q.2).
 *
 * UX Phase 1 simple (décision PO 9.Q.2) :
 *  - Header : titre scope-aware (project / field / cell)
 *  - List   : notes existantes (plus récentes en haut), display + edit/delete
 *             inline par note
 *  - Footer : textarea "nouvelle note" + bouton Ajouter
 *  - Texte libre seulement (1..4000 chars). Pas de tags / threading /
 *    version history / IA — anti-scope strict.
 *
 * Scopes possibles (3 niveaux Phase 1) :
 *  - project : `{ kind: "project" }`
 *  - field   : `{ kind: "field", field_key, field_label }`
 *  - cell    : `{ kind: "cell", field_key, field_label, submission_id, insurer_name }`
 *
 * a11y :
 *  - `role="dialog"` + `aria-modal=true` + `aria-labelledby` (titre)
 *  - Escape → close (sauf pendant submit en cours)
 *  - Focus initial : textarea "nouvelle note" (input principal)
 *  - Backdrop click → close (sauf pendant submit)
 *  - z-[60] pour passer au-dessus du maximize overlay z-50
 *
 * Aucune note ne modifie les valeurs extraites. Aucune réutilisation
 * automatique (DN-13 = re-confirmation manuelle obligatoire).
 */

import type {
  ComparisonNoteRow,
  CreateComparisonNoteInput,
  UpdateComparisonNoteInput,
} from "@kollectivia/shared";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import {
  useComparisonNotes,
  useCreateComparisonNote,
  useDeleteComparisonNote,
  useUpdateComparisonNote,
} from "../../../lib/queries/comparison-notes";

const BODY_MAX = 4000;

// -----------------------------------------------------------------------------
// Types — Scope + Props
// -----------------------------------------------------------------------------

export type ComparisonNotesScope =
  | { readonly kind: "project" }
  | {
      readonly kind: "field";
      readonly field_key: string;
      readonly field_label: string;
    }
  | {
      readonly kind: "cell";
      readonly field_key: string;
      readonly field_label: string;
      readonly submission_id: string;
      readonly insurer_name: string;
    };

type Props = {
  readonly projectId: string;
  readonly scope: ComparisonNotesScope;
  readonly onClose: () => void;
};

// -----------------------------------------------------------------------------
// Composant
// -----------------------------------------------------------------------------

export function ComparisonNotesDialog({ projectId, scope, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const newNoteFieldId = useId();
  const newNoteRef = useRef<HTMLTextAreaElement | null>(null);

  // Fetch — filtré côté serveur par scope (field/cell). Project scope
  // re-filtré côté client (sans filtre serveur — récupère tout puis matche).
  const listParams = useMemo(() => {
    if (scope.kind === "project") {
      return { projectId };
    }
    if (scope.kind === "field") {
      return { projectId, fieldKey: scope.field_key };
    }
    return {
      projectId,
      fieldKey: scope.field_key,
      submissionId: scope.submission_id,
    };
  }, [projectId, scope]);

  const notesQ = useComparisonNotes(listParams);

  // Filter client-side : pour project scope, on garde uniquement les notes
  // avec field_key NULL ET submission_id NULL. Pour field, field_key match ET
  // submission_id NULL. Pour cell, le serveur a déjà filtré strictement.
  const filtered = useMemo(() => {
    const items = notesQ.data?.items ?? [];
    if (scope.kind === "project") {
      return items.filter(
        (n) => n.field_key === null && n.submission_id === null,
      );
    }
    if (scope.kind === "field") {
      return items.filter(
        (n) => n.field_key === scope.field_key && n.submission_id === null,
      );
    }
    return items.filter(
      (n) =>
        n.field_key === scope.field_key &&
        n.submission_id === scope.submission_id,
    );
  }, [notesQ.data, scope]);

  const createM = useCreateComparisonNote();
  const updateM = useUpdateComparisonNote();
  const deleteM = useDeleteComparisonNote();

  const anyPending =
    createM.isPending || updateM.isPending || deleteM.isPending;

  // State formulaire — nouvelle note
  const [newBody, setNewBody] = useState("");

  // State formulaire — édition inline d'une note existante
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState("");

  // Focus initial sur textarea nouvelle note
  useEffect(() => {
    queueMicrotask(() => newNoteRef.current?.focus());
  }, []);

  // Escape close (sauf pendant submit)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !anyPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, anyPending]);

  // Submit handlers
  const trimmedNew = newBody.trim();
  const canSubmitNew =
    trimmedNew.length >= 1 && trimmedNew.length <= BODY_MAX && !anyPending;

  const onSubmitNew = () => {
    if (!canSubmitNew) return;
    const input: CreateComparisonNoteInput = {
      project_id: projectId,
      field_key:
        scope.kind === "project" ? null : scope.field_key,
      submission_id:
        scope.kind === "cell" ? scope.submission_id : null,
      body: trimmedNew,
    };
    createM.mutate(
      { input },
      {
        onSuccess: () => {
          setNewBody("");
        },
      },
    );
  };

  const trimmedEdit = editingBody.trim();
  const canSubmitEdit =
    editingId !== null &&
    trimmedEdit.length >= 1 &&
    trimmedEdit.length <= BODY_MAX &&
    !anyPending;

  const onStartEdit = (note: ComparisonNoteRow) => {
    setEditingId(note.id);
    setEditingBody(note.body);
  };

  const onCancelEdit = () => {
    setEditingId(null);
    setEditingBody("");
  };

  const onSubmitEdit = () => {
    if (!canSubmitEdit || editingId === null) return;
    const input: UpdateComparisonNoteInput = { body: trimmedEdit };
    updateM.mutate(
      { noteId: editingId, input },
      {
        onSuccess: () => {
          setEditingId(null);
          setEditingBody("");
        },
      },
    );
  };

  const onDelete = (note: ComparisonNoteRow) => {
    if (anyPending) return;
    // Confirm inline simple — pas de second dialog (UX Phase 1 décision PO).
    const ok = window.confirm(t("comparison_notes.dialog.delete.confirm"));
    if (!ok) return;
    deleteM.mutate({ noteId: note.id });
  };

  // Title scope-aware
  const dialogTitle = useMemo(() => {
    if (scope.kind === "project") {
      return t("comparison_notes.dialog.title.project");
    }
    if (scope.kind === "field") {
      return t("comparison_notes.dialog.title.field", {
        field_label: scope.field_label,
      });
    }
    return t("comparison_notes.dialog.title.cell", {
      field_label: scope.field_label,
      insurer_name: scope.insurer_name,
    });
  }, [scope, t]);

  // Aggregate API error message bilingue
  const apiErrorMsg = (() => {
    const err = createM.error ?? updateM.error ?? deleteM.error;
    if (err instanceof ApiException) {
      return getApiErrorMessage(err, lang) || t("errors.generic");
    }
    if (createM.isError || updateM.isError || deleteM.isError) {
      return t("errors.generic");
    }
    return null;
  })();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!anyPending) onClose();
        }}
      />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-slate-200 motion-safe:animate-fade-in">
        <header className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2
            id={titleId}
            className="break-words pr-2 text-base font-semibold text-slate-900"
          >
            {dialogTitle}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={anyPending}
            aria-label={t("common.close")}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 disabled:opacity-50 touch-manipulation"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 3l8 8M11 3l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Liste des notes existantes */}
          {notesQ.isLoading && !notesQ.data && (
            <p
              aria-live="polite"
              className="py-4 text-center text-sm text-slate-500"
            >
              {t("common.progress")}…
            </p>
          )}

          {notesQ.isSuccess && filtered.length === 0 && (
            <p className="py-6 text-center text-sm text-slate-500">
              {t("comparison_notes.dialog.empty_state")}
            </p>
          )}

          {filtered.length > 0 && (
            <ul className="space-y-3">
              {filtered.map((note) => {
                const isEditing = editingId === note.id;
                return (
                  <li
                    key={note.id}
                    className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-3"
                  >
                    {isEditing ? (
                      <div className="space-y-2">
                        <textarea
                          rows={3}
                          maxLength={BODY_MAX}
                          value={editingBody}
                          onChange={(e) => setEditingBody(e.target.value)}
                          disabled={anyPending}
                          className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
                        />
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-500 tabular-nums">
                            {trimmedEdit.length}/{BODY_MAX}
                          </span>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={onCancelEdit}
                              disabled={anyPending}
                              className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 disabled:opacity-50 touch-manipulation"
                            >
                              {t("common.cancel")}
                            </button>
                            <button
                              type="button"
                              onClick={onSubmitEdit}
                              disabled={!canSubmitEdit}
                              aria-busy={updateM.isPending}
                              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:bg-slate-300 touch-manipulation"
                            >
                              {updateM.isPending
                                ? t("comparison_notes.dialog.edit.submitting")
                                : t("comparison_notes.dialog.edit.save_button")}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="whitespace-pre-wrap break-words text-sm text-slate-800">
                          {note.body}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
                          <span className="truncate">
                            {note.created_by_user_display_name ??
                              note.created_by_agent_display_name ??
                              t("comparison_notes.dialog.author_unknown")}
                          </span>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => onStartEdit(note)}
                              disabled={anyPending}
                              className="rounded px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 disabled:opacity-50 touch-manipulation"
                            >
                              {t("comparison_notes.dialog.edit.button")}
                            </button>
                            <button
                              type="button"
                              onClick={() => onDelete(note)}
                              disabled={anyPending}
                              className="rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 disabled:opacity-50 touch-manipulation"
                            >
                              {t("comparison_notes.dialog.delete.button")}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {apiErrorMsg && (
            <div
              role="alert"
              className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        {/* Formulaire ajout note */}
        <footer className="shrink-0 border-t border-slate-100 px-5 py-4">
          <label
            htmlFor={newNoteFieldId}
            className="mb-1.5 block text-xs font-medium text-slate-700"
          >
            {t("comparison_notes.dialog.add.label")}
          </label>
          <textarea
            id={newNoteFieldId}
            ref={newNoteRef}
            rows={3}
            maxLength={BODY_MAX}
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            disabled={anyPending}
            placeholder={t("comparison_notes.dialog.add.placeholder")}
            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
          />
          <div className="mt-1.5 flex items-center justify-between text-xs text-slate-500">
            <span className="tabular-nums">
              {trimmedNew.length}/{BODY_MAX}
            </span>
            <button
              type="button"
              onClick={onSubmitNew}
              disabled={!canSubmitNew}
              aria-busy={createM.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:bg-slate-300 touch-manipulation"
            >
              {createM.isPending
                ? t("comparison_notes.dialog.add.submitting")
                : t("comparison_notes.dialog.add.button")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
